/**
 * Pure presentation/telemetry helpers for edge bots — the queue-distance heat
 * ramp, posture tone, queue math, climb/ETA motion derivation, and formatters.
 * Extracted from the Recycler page so the Position Tracker bot zones reuse the
 * exact same intelligence + visual vocabulary. No React, no I/O.
 */
import type { AgentStats } from './edge';

export const FRONT_AT = 20; // <= contracts ahead → at the front (fill / adverse-selection risk)
export const FRONT_NEAR = 50; // <= contracts ahead → near the front
export const QUEUE_SCALE = 300; // contracts reference for the fill anchor (full = at the front)
export const POLL_MS = 1000;
export const HIST = 24; // poll samples kept per agent for climb / ETA / sparkline

export type ViewMode = 'compact' | 'card' | 'table';
export type SortKey = 'front' | 'eta' | 'net' | 'errors';
export type Sample = { ms: number; front: number };
export type Motion = { climb: number; eta: number; spark: number[] };

// ── queue-distance heat: the ONLY rainbow axis. RED far from the front → GREEN
//    as the line nears fill. `rgb` is the raw triplet for full-row gradients.
export function heat(ahead: number): { text: string; fill: string; bar: string; rgb: string } {
  if (ahead < 0) return { text: 'text-muted-foreground', fill: 'bg-muted-foreground/10', bar: 'bg-muted-foreground/50', rgb: '148 163 184' };
  if (ahead <= FRONT_AT) return { text: 'text-green-400', fill: 'bg-green-500/12', bar: 'bg-green-500', rgb: '34 197 94' };
  if (ahead <= FRONT_NEAR) return { text: 'text-amber-400', fill: 'bg-amber-500/12', bar: 'bg-amber-500', rgb: '245 158 11' };
  return { text: 'text-red-400', fill: 'bg-red-500/12', bar: 'bg-red-500', rgb: '239 68 68' };
}

// ── posture: bot health, a SEPARATE channel (dot + pill, never the heat ramp).
export function postureTone(p: string): { dot: string; cls: string; label: string } {
  switch (p) {
    case 'green': return { dot: 'bg-green-500', cls: 'text-green-400', label: 'healthy' };
    case 'yellow': return { dot: 'bg-amber-500', cls: 'text-amber-400', label: 'working' };
    case 'orange': return { dot: 'bg-orange-500', cls: 'text-orange-400', label: 'strained' };
    case 'red': return { dot: 'bg-red-500', cls: 'text-red-400', label: 'trouble' };
    default: return { dot: 'bg-muted-foreground', cls: 'text-muted-foreground', label: p || '—' };
  }
}

export function frontOf(a: AgentStats): number {
  const r = (a.resting ?? []).map((o) => o.queuePosition).filter((q) => q >= 0);
  return r.length ? Math.min(...r) : -1;
}
export function restingTotal(a: AgentStats): number {
  return (a.resting ?? []).reduce((s, o) => s + o.sizeContracts, 0);
}
export function progressPct(front: number): number {
  if (front < 0) return 0;
  return Math.max(2, Math.min(100, Math.round((1 - Math.min(front, QUEUE_SCALE) / QUEUE_SCALE) * 100)));
}
export function legsOf(a: AgentStats): { side: 'bid' | 'ask'; size: number; price: number }[] {
  const r = a.resting ?? [];
  const out: { side: 'bid' | 'ask'; size: number; price: number }[] = [];
  for (const side of ['bid', 'ask'] as const) {
    const os = r.filter((o) => o.side === side);
    if (os.length) out.push({ side, size: os.reduce((x, o) => x + o.sizeContracts, 0), price: os[0].priceCents });
  }
  return out;
}
export function uptime(startedAtMs: number): string {
  if (!startedAtMs) return '—';
  const s = Math.max(0, Math.round((Date.now() - startedAtMs) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}m`;
}

export function deriveMotion(hist: Sample[] | undefined): Motion {
  const valid = (hist ?? []).filter((s) => s.front >= 0);
  if (valid.length < 2) return { climb: 0, eta: -1, spark: [] };
  const a = valid[0];
  const b = valid[valid.length - 1];
  const dt = (b.ms - a.ms) / 1000;
  const climb = dt > 0 ? (a.front - b.front) / dt : 0; // contracts/sec advancing toward the front
  const eta = climb > 0.02 && b.front >= 0 ? b.front / climb : -1;
  const spark = valid.map((s) => Math.max(0, QUEUE_SCALE - Math.min(s.front, QUEUE_SCALE))); // rising = climbing
  return { climb, eta, spark };
}

export const fmtClimb = (c: number) => (Math.abs(c) < 0.05 ? '0/s' : `${c > 0 ? '+' : ''}${c.toFixed(1)}/s`);
export const fmtEta = (e: number) => (e < 0 ? '—' : e < 90 ? `~${Math.round(e)}s` : `~${Math.round(e / 60)}m`);
export const fmtVel = (v?: number) => (v == null ? '—' : `${v.toFixed(1)}¢/s`);
export const fmtNet = (n: number) => `${n > 0 ? '+' : ''}${n}`;
export function markout5s(a: AgentStats): string {
  const m = (a.markout ?? []).find((x) => x.horizonMs === 5000);
  return m && m.fills > 0 ? `${Math.round(m.rightSideRate * 100)}%` : '—';
}
